import { wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import type { ColumnPosition, PorchColumnPattern } from "building-types/columns/porchColumnPattern";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import { BuiltinDoorId } from "building-types/doors/BuiltinDoorId";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { HalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { TargetedPorchSettings } from "building-types/porches/porch";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomEnclosure, RoomType } from "building-types/rooms/roomTypesEnum";
import type { PorchSettingsBase, ColonnadeTerminationRule } from "building-types/settings/porchSettingsTypes";
import { ColonnadeTerminationType } from "building-types/settings/settingsTypesEnum";
import type { StructureSettings } from "building-types/settings/structureSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import { WallType } from "building-types/walls/wallEnums";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { SolidWall } from "building-types/walls/wallTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { edgeDataRemap } from "doc/provenance/edgeDataTools";
import { closestPointPolyLine2, polyLine2OverlappingEdgeMapSlow } from "geom/closestPoint";
import { ANGLE_EPSILON } from "geom/core";
import { intersectRay2PolyLine2Array, ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import { line2MidPt } from "geom/line2";
import {
  planarGraphAdjacentHalfEdge,
  planarGraphHalfEdgeNormal,
  planarGraphHalfEdgeStripBreakByPredicate,
  planarGraphHalfEdgeStripToPolyLine2,
  planarGraphHalfEdgeTangent,
  planarGraphHalfEdgeToLine2,
  planarGraphNextHalfEdgeCCWAroundStartVertex,
  planarGraphNextHalfEdgeInLoop,
  planarGraphPrevHalfEdgeInLoop,
  planarGraphTraceFaceGroupPerimeter,
} from "geom/planarGraph";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeLength,
  polyLine2EdgeTangent,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2IsClosed,
  polyLine2MergeCollinearEdges,
  polyLine2Reverse,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { ray2New } from "geom/ray2";
import type { Ray2 } from "geom/ray2Types";
import type { Vec2 } from "geom/vec2";
import { vec2AngleBetween, vec2AngleBetweenCW, vec2Dot, vec2Perp } from "geom/vec2";
import { getWallPath, wallIsBoundary } from "../walls/wall";
import { solidWallFramingLayer, solidWallSubstrateLayer } from "../walls/wallLayers";
import { wallIntersectionTypesAtStripEnds } from "./porchColonnadeEnds";

const MAX_SEGMENT_OFFSET_MULT = 2;

interface PorchSegment {
  readonly segment: PolyLine2;
  readonly prevEdge: PolyLine2 | null;
  readonly nextEdge: PolyLine2 | null;
  readonly segmentHalfEdges: (number | undefined)[][];
}

interface PorchEdgeLayout {
  readonly segments: PorchSegment[];
  readonly start: ColonnadeTerminationType;
  readonly end: ColonnadeTerminationType;
  readonly halfEdgeIndices: number[][];
}

export function makePorchEdgeLayout(
  roomSet: Room[],
  wallSystem: WallSystem,
  roomIndex: RoomIndex,
  colonnadeRules: ColonnadeTerminationRule[],
  levelColumnPatterns: HalfEdgeAssociationTable<PorchColumnPattern>,
  targetedPorchSettings: TargetedPorchSettings,
): PorchEdgeLayout[] {
  const halfEdgeIndicesWithColumnPatterns = markHalfEdgesWithColumnPatterns(levelColumnPatterns);
  const wallIndex = wallSystem.wallIndex;
  const planarGraph = wallSystem.wallIndex.planarGraph.planarGraph;
  const includedFaceIndices = roomSet.map((x) =>
    wallIndex.planarGraph.polygonToPlanarGraphFace(roomIndex.roomLevelIndices[x.id]),
  );

  const loops = planarGraphTraceFaceGroupPerimeter(planarGraph, includedFaceIndices);

  const exteriorSegments: PorchEdgeLayout[] = [];
  for (const halfEdges of loops.faceHalfEdges) {
    const strips = planarGraphHalfEdgeStripBreakByPredicate(planarGraph, halfEdges, (halfEdge) => {
      // for each half edge, look up the wall, check if the wall is a boundary edge
      const wallId = wallSystem.halfEdgeToWall[halfEdge];
      const wall = wallSystem.walls[wallId];
      const { rightRoom, leftRoom } = getWallPath(wall);

      const isPorchRoomExcluded = (room: Room) => {
        const roomId = getSourceRoomId(room);
        const porchSettings = targetedPorchSettings[roomId];
        if (!porchSettings) {
          return false;
        }
        return (
          (room?.type === RoomType.FRONT_PORCH && porchSettings?.excludeFromPorchMerge) ||
          (room?.type === RoomType.REAR_PORCH && porchSettings?.excludeFromPorchMerge)
        );
      };

      const includeEdgeBetweenPorchRooms = (rightRoom: Room | undefined, leftRoom: Room | undefined) => {
        if (!rightRoom || !leftRoom) {
          return false;
        }

        if (rightRoom.type === leftRoom.type) {
          return isPorchRoomExcluded(rightRoom) || isPorchRoomExcluded(leftRoom);
        }

        return false;
      };

      return (
        wallIsBoundary(wallSystem.walls[wallId]) ||
        rightRoom?.attributes.roomEnclosure === RoomEnclosure.NONE ||
        leftRoom?.attributes.roomEnclosure === RoomEnclosure.NONE ||
        !!includeEdgeBetweenPorchRooms(rightRoom, leftRoom)
      );
    });

    const { start, end } = wallIntersectionTypesAtStripEnds(strips, planarGraph, wallSystem);

    let startTermType: ColonnadeTerminationType = ColonnadeTerminationType.NO_COLUMN;
    let endTermType: ColonnadeTerminationType = ColonnadeTerminationType.NO_COLUMN;

    if (colonnadeRules) {
      for (const rule of colonnadeRules) {
        if (rule.endType === start) {
          startTermType = rule.terminationType;
        }
        if (rule.endType === end) {
          endTermType = rule.terminationType;
        }
      }
    }

    // if porch  exists, do not merge collinear edges.
    const segments: PorchSegment[] = strips.map((x) => {
      let hasColumnPattern = false;

      for (const heIndex of x) {
        if (halfEdgeIndicesWithColumnPatterns[heIndex]) {
          hasColumnPattern = true;
        }
      }

      // find the previous and next half edges from the original loop
      const prevHalfEdge = planarGraphPrevHalfEdgeInLoop(halfEdges, x[0]);
      const nextHalfEdge = planarGraphNextHalfEdgeInLoop(halfEdges, x[x.length - 1]);

      const prevEdge = prevHalfEdge >= 0 ? planarGraphHalfEdgeToLine2(planarGraph, prevHalfEdge) : null;
      const nextEdge = nextHalfEdge >= 0 ? planarGraphHalfEdgeToLine2(planarGraph, nextHalfEdge) : null;

      // build the segment
      const rawSegment = planarGraphHalfEdgeStripToPolyLine2(planarGraph, x);
      const segment = !hasColumnPattern ? polyLine2MergeCollinearEdges(polyLine2Copy(rawSegment)) : rawSegment;
      const halfEdgeArr = !hasColumnPattern
        ? edgeDataRemap(
            polyLine2OverlappingEdgeMapSlow(rawSegment, segment),
            x.map((he) => [he]),
            (a, b) => [...a, ...b],
            polyLine2EdgeCount(segment),
            undefined,
          )
        : x.map((he) => [he]);

      return {
        segment,
        prevEdge,
        nextEdge,
        segmentHalfEdges: halfEdgeArr,
      };
    });

    exteriorSegments.push({
      segments,
      start: startTermType,
      end: endTermType,
      halfEdgeIndices: strips,
    });
  }

  return exteriorSegments;
}

function markHalfEdgesWithColumnPatterns(levelColumnPatterns: HalfEdgeAssociationTable<PorchColumnPattern>) {
  const halfEdgeIndicesWithColumnPatterns: { [heIndex: number]: boolean } = {};
  for (const patternId in levelColumnPatterns) {
    const adjacentHe = planarGraphAdjacentHalfEdge(parseInt(patternId));
    if (adjacentHe !== undefined) {
      halfEdgeIndicesWithColumnPatterns[adjacentHe] = true;
    }
  }
  return halfEdgeIndicesWithColumnPatterns;
}

export interface DoorProjectionRay {
  readonly door: GeneratedDoorPlacement;
  readonly ray: Ray2;
}

export function makeDoorProjectionRays(rooms: Room[], wallIndex: WallIndex): DoorProjectionRay[] {
  const doorRays: DoorProjectionRay[] = [];

  for (const room of rooms) {
    for (const wall of wallIndex.wallsInRoom(room)) {
      if (wall.wallType !== WallType.SOLID) {
        continue;
      }
      const { startX, startY, endX, endY } = getWallPath(wall);
      for (const door of wall.doorPlacements) {
        if (door.descriptor.id === BuiltinDoorId.NONE) {
          continue;
        }
        const wallPoly = polyLine2ByTwoPoints(startX, startY, endX, endY);
        assertIsNotNullOrUndefined(door.opening);
        const oPl = wallOpeningPlanExtents(door.opening);
        const [dX, dY] = line2MidPt(oPl[0], oPl[1], oPl[2], oPl[3]);
        const [tX, tY] = polyLine2EdgeTangent(wallPoly, 0);
        const [rX, rY] = vec2Perp(tX, tY);
        doorRays.push({
          door,
          ray: ray2New(dX, dY, rX, rY),
        });
      }
    }
  }

  return doorRays;
}

export interface EdgeIntersectionParams {
  [edgeIndex: number]: { door: GeneratedDoorPlacement; param: number }[];
}

export function intersectDoorProjectionRays(rays: DoorProjectionRay[], segment: PolyLine2): EdgeIntersectionParams {
  const edgeIntParams: EdgeIntersectionParams = {};

  for (const doorRay of rays) {
    const {
      door,
      ray: { originX, originY, dirX, dirY },
    } = doorRay;
    const intersections = intersectRay2PolyLine2Array([segment], originX, originY, dirX, dirY);
    ray2PolyLine2IntersectionMakeUnique(intersections);

    if (intersections.length === 0 || intersections[0] === undefined) {
      continue;
    }

    const { x, y } = intersections[0];
    const { edgeIndex, t } = closestPointPolyLine2(segment, x, y);
    const params = edgeIntParams[edgeIndex] || [];
    params.push({ door, param: t });
    edgeIntParams[edgeIndex] = params;
  }

  return edgeIntParams;
}

export function placeColumnIfUnsupportedVertexAbove(
  structureSettings: StructureSettings,
  unsupportedCornerAbovePts: Vec2[],
  segment: PolyLine2,
) {
  const unsupportedCornerAboveParams: number[] = [];
  if (
    structureSettings?.maxUnsupportedCornerSpan &&
    unsupportedCornerAbovePts &&
    unsupportedCornerAbovePts.length > 0
  ) {
    for (const [unsupportedPtX, unsupportedPtY] of unsupportedCornerAbovePts) {
      const cp = closestPointPolyLine2(
        polyLine2New([segment[0], segment[1], segment[2], segment[3]]),
        unsupportedPtX,
        unsupportedPtY,
      );
      if (cp && cp.dist < 10) {
        // unsupportedCornerAboveParams.push(cp.t);
      }
    }
  }
  return unsupportedCornerAboveParams;
}

export function formatPorchExteriorSegmentsAndCoordinateColumnPositions({
  segments,
  porchSettings,
  edgeIndexToColumnPositionsTable,
  halfEdgeIndices,
  wallSettings,
  wallSystem,
  wallDatumForPorchSegmentTermination,
}: {
  segments: PorchSegment[];
  porchSettings: PorchSettingsBase;
  wallSettings: WallSettings;
  edgeIndexToColumnPositionsTable: ((ColumnPosition[] | null)[] | null)[];
  halfEdgeIndices: number[][];
  wallSystem: WallSystem;
  /**
   * wallDatumForPorchSegmentTermination is linked to the exteriorDimension datums.
   * This value needs to align to the exteriorDimensionDatum in order to avoid gaps or
   * overlaps between the exterior dimensions and the porch column dimension strings
   */
  wallDatumForPorchSegmentTermination: WallLateralDatumAnchor;
}): { segment: PolyLine2; segmentHalfEdges: (number | undefined)[][] }[] {
  const exteriorSegmentsA = segments.map(({ segment, prevEdge, nextEdge, segmentHalfEdges }) => {
    assertIsNotNullOrUndefined(prevEdge);
    assertIsNotNullOrUndefined(nextEdge);
    return {
      segment: polyLine2Reverse(segment),
      prevEdge: polyLine2Reverse(nextEdge),
      nextEdge: polyLine2Reverse(prevEdge),
      segmentHalfEdges: segmentHalfEdges.reverse(),
    };
  });

  let exteriorSegments: PorchSegment[];

  if (porchSettings.endSegmentJoinTolerance !== undefined) {
    exteriorSegments = [];
    const endSegmentJoinTolerance = porchSettings.endSegmentJoinTolerance;

    for (let i = 0; i < exteriorSegmentsA.length; i++) {
      const exteriorSegmentA = exteriorSegmentsA[i];
      const { prevEdge, nextEdge, segmentHalfEdges } = exteriorSegmentA;
      let { segment } = exteriorSegmentA;

      let segmentColumnPositions = edgeIndexToColumnPositionsTable[i];

      let removeStart = false;
      let removeEnd = false;

      if (segment.length > 4 && polyLine2EdgeLength(segment, 0) < endSegmentJoinTolerance) {
        segment = segment.slice(2);
        removeStart = true;
      }

      if (polyLine2EdgeLength(segment, polyLine2EdgeCount(segment)) < endSegmentJoinTolerance) {
        segment = segment.slice(0, segment.length - 2);
        removeEnd = true;
      }

      if (segment.length >= 4) {
        // keep edge column positions indices in sync with edge indices
        if (removeStart) {
          if (segmentColumnPositions) {
            segmentColumnPositions = segmentColumnPositions.slice(1);
          }
        }
        if (removeEnd) {
          if (segmentColumnPositions) {
            segmentColumnPositions = segmentColumnPositions.slice(segmentColumnPositions.length - 1);
          }
        }
        exteriorSegments.push({
          segment,
          prevEdge: removeStart ? null : prevEdge,
          nextEdge: removeEnd ? null : nextEdge,
          segmentHalfEdges,
        });
      }

      edgeIndexToColumnPositionsTable[i] = segmentColumnPositions;
    }
  } else {
    exteriorSegments = exteriorSegmentsA;
  }

  function getWallOffsetForPorchCollonade(halfEdge: number) {
    const planarGraph = wallSystem.wallIndex.planarGraph.planarGraph;
    const [tx, ty] = planarGraphHalfEdgeTangent(planarGraph, halfEdge);
    const [nx, ny] = planarGraphHalfEdgeNormal(planarGraph, halfEdge, true);

    // find a solid wall whose direction is closest to the outgoing porch edge by comparing
    // the dot product of the porch edge tangent and the outgoing half edges emanating from
    // its start vertex
    let largestDot = -1;
    let closestSolidWallHalfEdgeIndex = -1;
    let leftSide = false;

    planarGraphNextHalfEdgeCCWAroundStartVertex(planarGraph, halfEdge, (candidateHalfEdge) => {
      const wallIndex = wallSystem.halfEdgeToWall[candidateHalfEdge];
      const [txcand, tycand] = planarGraphHalfEdgeTangent(planarGraph, candidateHalfEdge);

      const wall = wallSystem.walls[wallIndex];

      if (wall?.wallType === WallType.SOLID) {
        const dot = vec2Dot(tx, ty, txcand, tycand);
        if (dot > largestDot) {
          closestSolidWallHalfEdgeIndex = candidateHalfEdge;
          largestDot = dot;
          leftSide = vec2Dot(txcand, tycand, nx, ny) > 0;
        }
      }

      return false;
    });

    if (closestSolidWallHalfEdgeIndex === -1) {
      return 0;
    }

    const wallIndex = wallSystem.halfEdgeToWall[closestSolidWallHalfEdgeIndex];
    const wall = wallSystem.walls[wallIndex] as SolidWall;
    const wallMass =
      wallDatumForPorchSegmentTermination === WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING
        ? solidWallFramingLayer(wall)
        : solidWallSubstrateLayer(wall);

    // if the wall is on the left side of the outgoing half edge tangent, then
    // we get the wall offset on the right side of that wall and vice versa

    // Note, walls are oriented along the half edge identified in the path, we need
    // to correct the offset for this to get the correct offset side
    if (leftSide) {
      return wallMass.path.halfEdgeIndex === closestSolidWallHalfEdgeIndex ? wallMass.rightOffset : wallMass.leftOffset;
    } else {
      return wallMass.path.halfEdgeIndex === closestSolidWallHalfEdgeIndex ? wallMass.leftOffset : wallMass.rightOffset;
    }
  }

  function adjustSegmentOffset(baseOffset: number, porchSegment: PorchSegment, isEnd: boolean) {
    const { segment, prevEdge, nextEdge } = porchSegment;

    if ((isEnd && !nextEdge) || (!isEnd && !prevEdge)) {
      return baseOffset;
    }

    let t0x: number, t0y: number;
    if (isEnd) {
      [t0x, t0y] = polyLine2EdgeTangent(segment, polyLine2EdgeCount(segment) - 1, true);
    } else {
      assertIsNotNullOrUndefined(prevEdge);
      [t0x, t0y] = polyLine2EdgeTangent(prevEdge, 0, true);
    }

    let t1x: number, t1y: number;
    if (isEnd) {
      assertIsNotNullOrUndefined(nextEdge);
      [t1x, t1y] = polyLine2EdgeTangent(nextEdge, 0);
    } else {
      [t1x, t1y] = polyLine2EdgeTangent(segment, 0);
    }

    const angle = Math.min(
      Math.abs(vec2AngleBetween(t0x, t0y, t1x, t1y)),
      Math.abs(vec2AngleBetweenCW(t0x, t0y, t1x, t1y)),
    );

    // avoid infinite tangent
    if (Math.abs(angle - Math.PI) < ANGLE_EPSILON) {
      return baseOffset;
    }

    const tan = Math.tan(angle / 2);
    const sign = Math.sign(tan);
    const mag = Math.min(Math.abs(tan), MAX_SEGMENT_OFFSET_MULT);

    return baseOffset * sign * mag;
  }

  if (porchSettings.endSegmentJoinTolerance === undefined) {
    // in this case, we can rely on the half edges to determine the collonade offset
    for (let i = 0, l = exteriorSegments.length; i < l; i++) {
      const exteriorSegment = exteriorSegments[i];
      const { segment } = exteriorSegment;

      if (!polyLine2IsClosed(segment)) {
        // the segments are reversed
        const endHalfEdge = halfEdgeIndices[i][0];
        const startHalfEdge = planarGraphAdjacentHalfEdge(halfEdgeIndices[i][halfEdgeIndices[i].length - 1]);

        let startOffset = getWallOffsetForPorchCollonade(startHalfEdge);
        let endOffset = getWallOffsetForPorchCollonade(endHalfEdge);

        startOffset = adjustSegmentOffset(startOffset, exteriorSegment, false);
        endOffset = adjustSegmentOffset(endOffset, exteriorSegment, true);

        polyLine2ExtendStart(segment, -startOffset);
        polyLine2ExtendEnd(segment, -endOffset);
      }
    }
  } else {
    for (const exteriorSegment of exteriorSegments) {
      const { segment } = exteriorSegment;

      if (!polyLine2IsClosed(segment)) {
        const startOffset = adjustSegmentOffset(wallSettings.exteriorWallOffset, exteriorSegment, false);
        const endOffset = adjustSegmentOffset(wallSettings.exteriorWallOffset, exteriorSegment, true);

        polyLine2ExtendStart(segment, -startOffset);
        polyLine2ExtendEnd(segment, -endOffset);
      }
    }
  }

  return exteriorSegments.map(({ segment, segmentHalfEdges }) => ({ segment, segmentHalfEdges }));
}

export function makeEdgeIndexToColumnPositionsTable(
  halfEdgeIndices: number[][],
  levelColumnPatterns: HalfEdgeAssociationTable<PorchColumnPattern>,
) {
  const edgeIndexToColumnPositionsTable: ((ColumnPosition[] | null)[] | null)[] = [];
  for (const list of halfEdgeIndices) {
    const segmentColumnPositions: (ColumnPosition[] | null)[] = [];
    const length = list?.length ?? 0;
    for (let i = 0; i < length; i++) {
      const he = list[length - i - 1];
      const adjacentHe = planarGraphAdjacentHalfEdge(he);

      const result = levelColumnPatterns[adjacentHe];
      if (!result || result.length === 0) {
        segmentColumnPositions.push(null);
        continue;
      }

      const columnPositions: ColumnPosition[] = [];
      for (const pattern of result) {
        columnPositions.push(...pattern.item.positions);
      }

      segmentColumnPositions.push(columnPositions);
    }

    segmentColumnPositions.length === 0
      ? edgeIndexToColumnPositionsTable.push(null)
      : edgeIndexToColumnPositionsTable.push(segmentColumnPositions);
  }

  return edgeIndexToColumnPositionsTable;
}
